if (!customElements.get('vt-feat-slider')) {
  class VtFeatSlider extends HTMLElement {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      this.destroy();
    }

    init() {
      this.destroy();

      this.track = this.querySelector('[data-track]');
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      if (!this.track) return;

      this.onPrev = () => this.scrollByCard(-1);
      this.onNext = () => this.scrollByCard(1);
      this.onScroll = () => this.syncArrows();
      this.onResize = () => this.syncArrows();

      this.prev?.addEventListener('click', this.onPrev);
      this.next?.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });

      this.bindDragScroll();

      requestAnimationFrame(() => this.syncArrows());
      setTimeout(() => this.syncArrows(), 120);
    }

    destroy() {
      this.prev?.removeEventListener('click', this.onPrev);
      this.next?.removeEventListener('click', this.onNext);
      this.track?.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.unbindDragScroll?.();
    }

    bindDragScroll() {
      const track = this.track;

      // Touch devices: use native momentum scroll (same as homepage tabs).
      // Custom pointer-drag fights vertical page scroll and feels janky on mobile.
      if (window.matchMedia('(hover: none), (pointer: coarse)').matches) {
        this.unbindDragScroll = () => {};
        return;
      }

      let isDown = false;
      let startX = 0;
      let startY = 0;
      let scrollLeft = 0;
      let dragAxis = null;

      const onPointerDown = (event) => {
        if (event.target.closest('a, button, input, select, textarea, label')) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        isDown = true;
        dragAxis = null;
        startX = event.clientX;
        startY = event.clientY;
        scrollLeft = track.scrollLeft;
      };

      const onPointerMove = (event) => {
        if (!isDown) return;

        const dx = event.clientX - startX;
        const dy = event.clientY - startY;

        if (!dragAxis) {
          if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
          dragAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          if (dragAxis === 'y') {
            isDown = false;
            track.classList.remove('is-dragging');
            return;
          }
          track.classList.add('is-dragging');
          track.setPointerCapture?.(event.pointerId);
        }

        if (dragAxis === 'x') {
          track.scrollLeft = scrollLeft - dx;
        }
      };

      const onPointerUp = (event) => {
        if (!isDown) return;
        isDown = false;
        dragAxis = null;
        track.classList.remove('is-dragging');
        track.releasePointerCapture?.(event.pointerId);
        this.syncArrows();
      };

      track.addEventListener('pointerdown', onPointerDown, { passive: true });
      track.addEventListener('pointermove', onPointerMove, { passive: false });
      track.addEventListener('pointerup', onPointerUp);
      track.addEventListener('pointercancel', onPointerUp);
      track.addEventListener('pointerleave', onPointerUp);

      this.unbindDragScroll = () => {
        track.removeEventListener('pointerdown', onPointerDown);
        track.removeEventListener('pointermove', onPointerMove);
        track.removeEventListener('pointerup', onPointerUp);
        track.removeEventListener('pointercancel', onPointerUp);
        track.removeEventListener('pointerleave', onPointerUp);
      };
    }

    scrollByCard(direction) {
      const card = this.track.querySelector('.vt-feat-card');
      if (!card) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 16;
      const amount = card.getBoundingClientRect().width + gap;
      this.track.scrollBy({ left: amount * direction, behavior: 'smooth' });
    }

    syncArrows() {
      if (!this.track) return;

      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth - 2);
      const atStart = this.track.scrollLeft <= 2;
      const atEnd = this.track.scrollLeft >= max;
      const hasOverflow = max > 2;

      if (this.prev) {
        this.prev.disabled = !hasOverflow || atStart;
        this.prev.classList.toggle('is-hidden', !hasOverflow);
      }
      if (this.next) {
        this.next.disabled = !hasOverflow || atEnd;
        this.next.classList.toggle('is-hidden', !hasOverflow);
      }
    }
  }

  customElements.define('vt-feat-slider', VtFeatSlider);
}

if (!window.initVtFeatSliders) {
  window.initVtFeatSliders = (root = document) => {
    root.querySelectorAll('vt-feat-slider').forEach((slider) => {
      if (typeof slider.init === 'function') slider.init();
    });
  };
}
